import { useState } from "react";
import { Sparkles } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { PageLoader, Spinner } from "@/components/ui/Spinner";
import {
  PageHeader,
  SectionCard,
  StatTile,
} from "@/features/power_projects/components/ui";

import { useEzBenchmarks, useEzGenerateInsight, useEzInsights } from "../hooks";
import type { BenchmarksParams, InsightRow } from "../types";
import {
  PROVINCES,
  Segmented,
  TAG_LABEL,
  fmtDate,
  fmtPln,
  provinceName,
} from "../ui";

const CPV_DIVISION_LABEL: Record<string, string> = {
  "45": "45 — roboty budowlane",
  "71": "71 — usługi projektowe/inżynierskie",
};

function Th({
  children,
  right,
}: {
  children: React.ReactNode;
  right?: boolean;
}) {
  return (
    <th
      className={`py-1.5 pr-3 font-medium text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] ${right ? "text-right" : "text-left"}`}
    >
      {children}
    </th>
  );
}

function BucketTable({
  rows,
  firstLabel,
  limit = 12,
}: {
  rows: {
    key: string;
    label: string;
    n: number;
    median_price: string | null;
    avg_offers: number | null;
  }[];
  firstLabel: string;
  limit?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? rows : rows.slice(0, limit);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--color-border)]">
            <Th>{firstLabel}</Th>
            <Th right>Postępowań</Th>
            <Th right>Mediana wygranej</Th>
            <Th right>Śr. ofert</Th>
          </tr>
        </thead>
        <tbody>
          {visible.map((r) => (
            <tr
              key={r.key}
              className="border-b border-[var(--color-border)] last:border-b-0"
            >
              <td className="py-1.5 pr-3 text-xs">{r.label}</td>
              <td className="py-1.5 pr-3 text-right tabular-nums text-xs">
                {r.n.toLocaleString("pl-PL")}
              </td>
              <td className="py-1.5 pr-3 text-right tabular-nums text-xs font-medium">
                {fmtPln(r.median_price)}
              </td>
              <td className="py-1.5 text-right tabular-nums text-xs">
                {r.avg_offers ?? "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > limit && (
        <button
          onClick={() => setExpanded((e) => !e)}
          className="text-[11px] text-rose-700 dark:text-rose-400 hover:underline mt-2"
        >
          {expanded ? "Zwiń" : `Pokaż wszystkie (${rows.length})`}
        </button>
      )}
    </div>
  );
}

function InsightCard({ insight }: { insight: InsightRow }) {
  const p = insight.payload;
  const section = (title: string, items?: string[], cls = "") =>
    (items?.length ?? 0) > 0 && (
      <div>
        <p className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mb-1">
          {title}
        </p>
        <ul className="space-y-0.5">
          {items!.map((x, i) => (
            <li key={i} className={`text-sm ${cls}`}>
              • {x}
            </li>
          ))}
        </ul>
      </div>
    );
  return (
    <div className="rounded-lg border border-[var(--color-border)] p-3">
      <p className="text-[10px] text-[var(--color-text-muted)] mb-1.5">
        {fmtDate(insight.created_at)}
        {Object.keys(insight.params).length > 0 && (
          <>
            {" "}
            · filtry:{" "}
            {Object.entries(insight.params)
              .map(([k, v]) => `${k}=${v}`)
              .join(", ")}
          </>
        )}
      </p>
      <p className="text-sm font-medium mb-2">{p.streszczenie}</p>
      <div className="grid md:grid-cols-2 gap-3">
        {section("Obserwacje", p.obserwacje)}
        {section("Okazje", p.okazje, "text-emerald-700 dark:text-emerald-400")}
        {section(
          "Ostrzeżenia",
          p.ostrzezenia,
          "text-amber-700 dark:text-amber-400",
        )}
        {section("Sugerowane działania", p.sugerowane_dzialania)}
      </div>
    </div>
  );
}

function InsightsSection({ params }: { params: BenchmarksParams }) {
  const insights = useEzInsights();
  const generate = useEzGenerateInsight();
  return (
    <SectionCard
      title="Insighty AI z historii"
      className="mb-4"
      action={
        <Button
          disabled={generate.isPending}
          onClick={() => generate.mutate(params)}
        >
          {generate.isPending
            ? "Generowanie…"
            : "Generuj insight dla tych filtrów"}
        </Button>
      }
    >
      {generate.isPending && (
        <p className="text-sm text-[var(--color-text-muted)] inline-flex items-center gap-2 mb-3">
          <Spinner /> EZ-ARON analizuje agregaty…
        </p>
      )}
      {generate.isError && (
        <p className="text-sm text-red-600 mb-3">
          Nie udało się wygenerować insightu.
        </p>
      )}
      {(insights.data?.length ?? 0) === 0 && !generate.isPending ? (
        <p className="text-sm text-[var(--color-text-muted)] inline-flex items-center gap-1.5">
          <Sparkles size={14} className="text-rose-500" />
          Jeszcze nic tu nie ma — ustaw filtry powyżej i wygeneruj pierwszy
          briefing rynkowy (jedno tanie wywołanie AI nad deterministycznymi
          agregatami).
        </p>
      ) : (
        <div className="space-y-3">
          {(insights.data ?? []).map((i) => (
            <InsightCard key={i.id} insight={i} />
          ))}
        </div>
      )}
    </SectionCard>
  );
}

export default function EzBenchmarksPage() {
  const [params, setParams] = useState<BenchmarksParams>({ months: 24 });
  const bench = useEzBenchmarks(params);
  const set = (patch: Partial<BenchmarksParams>) =>
    setParams((p) => ({ ...p, ...patch }));

  const d = bench.data;

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <PageHeader
        title="Benchmarki cen"
        subtitle="Wiedza rynkowa z rozstrzygniętych postępowań BZP — mediany cen wygranych, konkurencja, unieważnienia. Liczone deterministycznie, bez AI."
        backTo="/ezamowienia"
        backLabel="Oferty przetargowe"
      />

      {/* filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Segmented
          options={[
            { value: "", label: "P + W razem" },
            { value: "P", label: "Projekt" },
            { value: "PW", label: "Zaprojektuj i wybuduj" },
            { value: "W", label: "Wybuduj" },
          ]}
          value={params.work_mode ?? ""}
          onChange={(work_mode) => set({ work_mode })}
        />
        <select
          value={params.cpv ?? ""}
          onChange={(e) => set({ cpv: e.target.value })}
          className="text-xs border border-[var(--color-border)] rounded-lg px-2.5 py-1.5 bg-transparent"
        >
          <option value="">Wszystkie CPV</option>
          <option value="45">45 — roboty budowlane</option>
          <option value="71">71 — projektowe/inżynierskie</option>
        </select>
        <select
          value={params.province ?? ""}
          onChange={(e) => set({ province: e.target.value })}
          className="text-xs border border-[var(--color-border)] rounded-lg px-2.5 py-1.5 bg-transparent"
        >
          <option value="">Cała Polska</option>
          {PROVINCES.map((p) => (
            <option key={p.code} value={p.code}>
              {p.name}
            </option>
          ))}
        </select>
        <select
          value={params.tag ?? ""}
          onChange={(e) => set({ tag: e.target.value })}
          className="text-xs border border-[var(--color-border)] rounded-lg px-2.5 py-1.5 bg-transparent"
        >
          <option value="">Wszystkie branże</option>
          {Object.entries(TAG_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select
          value={String(params.months ?? 24)}
          onChange={(e) => set({ months: Number(e.target.value) })}
          className="text-xs border border-[var(--color-border)] rounded-lg px-2.5 py-1.5 bg-transparent"
        >
          <option value="6">Ostatnie 6 mies.</option>
          <option value="12">Ostatnie 12 mies.</option>
          <option value="24">Ostatnie 24 mies.</option>
          <option value="36">Ostatnie 36 mies.</option>
        </select>
      </div>

      <InsightsSection params={params} />

      {bench.isLoading || !d ? (
        <PageLoader />
      ) : d.totals.n_resolved === 0 ? (
        <SectionCard>
          <p className="py-6 text-center text-sm text-[var(--color-text-secondary)]">
            Brak rozstrzygniętych postępowań dla tych filtrów — poszerz kryteria
            albo poczekaj, aż load historyczny dociągnie starsze okna.
          </p>
        </SectionCard>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <StatTile
              label="Rozstrzygniętych"
              value={d.totals.n_resolved.toLocaleString("pl-PL")}
              hint={`w tym ${d.totals.n_with_price.toLocaleString("pl-PL")} z ceną`}
            />
            <StatTile
              label="Mediana wygranej"
              value={fmtPln(d.totals.median_price)}
              tone="ok"
              hint={`P25 ${fmtPln(d.totals.p25_price)} · P75 ${fmtPln(d.totals.p75_price)}`}
            />
            <StatTile
              label="Śr. liczba ofert"
              value={d.totals.avg_offers ?? "—"}
              tone="blue"
              hint="miara konkurencji"
            />
            <StatTile
              label="Unieważnienia"
              value={
                d.totals.annulled_pct != null
                  ? `${d.totals.annulled_pct}%`
                  : "—"
              }
              tone={
                d.totals.annulled_pct != null && d.totals.annulled_pct > 20
                  ? "warn"
                  : "default"
              }
              hint="odsetek postępowań"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <SectionCard title="Trend kwartalny">
              <BucketTable
                firstLabel="Kwartał"
                rows={d.trend.map((r) => ({
                  ...r,
                  key: r.quarter,
                  label: r.quarter,
                }))}
                limit={16}
              />
            </SectionCard>
            <SectionCard title="Województwa">
              <BucketTable
                firstLabel="Województwo"
                rows={d.by_province.map((r) => ({
                  ...r,
                  key: r.province,
                  label: provinceName(r.province),
                }))}
              />
            </SectionCard>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <SectionCard title="Działy CPV">
              <BucketTable
                firstLabel="Dział"
                rows={d.by_cpv_division.map((r) => ({
                  ...r,
                  key: r.division,
                  label: CPV_DIVISION_LABEL[r.division] ?? `CPV ${r.division}`,
                }))}
              />
            </SectionCard>
            <SectionCard title="Najczęściej wygrywający — mapa konkurencji">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[var(--color-border)]">
                      <Th>Wykonawca</Th>
                      <Th right>Wygrane</Th>
                      <Th right>Suma wygranych</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.top_winners.map((w) => (
                      <tr
                        key={w.winner}
                        className="border-b border-[var(--color-border)] last:border-b-0"
                      >
                        <td className="py-1.5 pr-3 text-xs">{w.winner}</td>
                        <td className="py-1.5 pr-3 text-right tabular-nums text-xs">
                          {w.wins}
                        </td>
                        <td className="py-1.5 text-right tabular-nums text-xs font-medium">
                          {fmtPln(w.total_value)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          </div>
        </>
      )}
    </div>
  );
}
